/* ==========================================================================
   MEDICORA — presentation behaviour for the Shopify theme

   Deliberately NOT a cart. Shopify owns the cart, the drawer and the checkout;
   every add-to-cart on the page is a real <form action="/cart/add">, so it
   works with JavaScript disabled and stays in sync with the theme.

   What lives here: the scroll reveal, the FAQ and ingredient accordions, the
   PDP gallery, the quantity stepper (mirrored into the form), the sticky buy
   bar and the aria-live announcer the review components look for.

   Scope note — this is what the 19 August regression was about. The page used
   to be one section, so one .medicora-page wrapper held everything and it was
   safe to scope every query to it. After the split there is one wrapper per
   section, and scoping to the first wrapper left every .reveal in every later
   section stuck at opacity:0 — the page rendered blank below the fold, and the
   accordions never got their listeners. So all queries now run against the
   document, and binding is idempotent per element rather than once per page,
   because each section injects this file and the theme editor re-renders
   sections one at a time.
   ========================================================================== */
(function () {
  "use strict";

  /* The Medicora pages carry a .medicora-page wrapper, but the cart page and the
     cart drawer do not — and the cart upsell lives in both. Bailing out on the
     wrapper alone meant the upsell button did nothing on /cart. Every binder
     below is selector-scoped and no-ops when its markup is absent, so the guard
     only has to establish that there is something here worth binding. */
  if (
    !document.querySelector(".medicora-page") &&
    !document.querySelector("[data-cart-upsell], .cart-page, .cart-drawer__content")
  ) {
    return;
  }

  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }

  /* Every binder marks what it has touched, so running twice — two sections
     injecting the script, or a section re-rendered in the editor — cannot
     attach a listener twice and step the quantity by two. */
  function claim(node, flag) {
    if (node.dataset[flag]) return false;
    node.dataset[flag] = "1";
    return true;
  }

  /* ------------------------------------------------------- announcer ---- */
  if (!window.MedicoraAnnounce) {
    var live = document.createElement("p");
    live.setAttribute("role", "status");
    live.setAttribute("aria-live", "polite");
    live.style.cssText =
      "position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap";
    document.body.appendChild(live);
    window.MedicoraAnnounce = function (message) {
      live.textContent = "";
      window.setTimeout(function () { live.textContent = message; }, 40);
    };
  }

  /* ---------------------------------------------------------- reveal ---- */
  var reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var revealObserver = null;

  function showAll(root) {
    qsa(".reveal", root).forEach(function (n) { n.classList.add("is-visible"); });
  }

  if (!("IntersectionObserver" in window) || reduce) {
    window.MedicoraRevealRefresh = showAll;
  } else {
    revealObserver = window.MedicoraRevealObserver || new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            window.MedicoraRevealObserver.unobserve(entry.target);
          }
        });
      }, { rootMargin: "0px 0px -8% 0px", threshold: 0.05 });
    window.MedicoraRevealObserver = revealObserver;
    window.MedicoraRevealRefresh = function (root) {
      qsa(".reveal:not(.is-visible)", root).forEach(function (n) {
        revealObserver.observe(n);
      });
    };
  }

  /* ------------------------------------------------------------- FAQ ---- */
  function bindAccordions(root) {
    qsa(".faq-item", root).forEach(function (item) {
      var btn = qs(".faq-q", item);
      var panel = qs(".faq-a", item);
      if (!btn || !panel || !claim(item, "medicoraFaq")) return;
      btn.addEventListener("click", function () {
        var open = btn.getAttribute("aria-expanded") === "true";
        btn.setAttribute("aria-expanded", String(!open));
        item.classList.toggle("is-open", !open);
        panel.hidden = open;
      });
    });
  }

  /* --------------------------------------------------------- gallery ---- */
  function bindGallery(root) {
    var thumbs = qsa("[data-gallery-thumb]", root);
    if (!thumbs.length) return;
    var main = qs("[data-gallery-main]", root) || qs("[data-gallery-main]");
    if (!main) return;
    thumbs.forEach(function (thumb) {
      if (!claim(thumb, "medicoraThumb")) return;
      thumb.addEventListener("click", function () {
        var img = qs("img", thumb);
        if (!img) return;
        main.src = img.getAttribute("src");
        main.alt = img.getAttribute("alt") || "";
        // the canonical packshot class must follow the image it describes
        main.classList.toggle(
          "peel-shot-product-image",
          img.classList.contains("peel-shot-product-image")
        );
        qsa("[data-gallery-thumb]").forEach(function (t) {
          t.setAttribute("aria-current", "false");
        });
        thumb.setAttribute("aria-current", "true");
      });
    });
  }

  /* --------------------------------------------------------- quantity --- */
  /* The input is the single source of truth: the -/+ buttons write to it, and
     typing into it is equally valid. The browser's own spinner arrows are
     hidden in CSS so there is only ever one visible way to change quantity. */
  function bindQuantity(root) {
    qsa("[data-qty-input]", root).forEach(function (qty) {
      if (!claim(qty, "medicoraQty")) return;
      var scope = qty.closest(".medicora-page") || document;
      var up = qs("[data-qty-up]", scope);
      var down = qs("[data-qty-down]", scope);
      var MIN = parseInt(qty.getAttribute("min"), 10) || 1;
      var MAX = parseInt(qty.getAttribute("max"), 10) || 99;

      function clamp(value) { return Math.max(MIN, Math.min(MAX, value)); }

      function sync(commit) {
        var raw = parseInt(qty.value, 10);
        /* while typing, an empty field is allowed — it is only clamped on blur,
           so the customer can clear it and type "12" without a fight */
        if (commit || !isNaN(raw)) qty.value = clamp(isNaN(raw) ? MIN : raw);
        var value = parseInt(qty.value, 10) || MIN;
        qsa("[data-qty-mirror]").forEach(function (input) { input.value = value; });
        if (down) {
          down.disabled = value <= MIN;
          down.setAttribute("aria-disabled", String(value <= MIN));
        }
        if (up) {
          up.disabled = value >= MAX;
          up.setAttribute("aria-disabled", String(value >= MAX));
        }
      }

      function step(by) {
        qty.value = clamp((parseInt(qty.value, 10) || MIN) + by);
        sync(true);
        window.MedicoraAnnounce("Quantity " + qty.value);
      }

      if (up && claim(up, "medicoraQtyUp")) {
        up.addEventListener("click", function () { step(1); });
      }
      if (down && claim(down, "medicoraQtyDown")) {
        down.addEventListener("click", function () { step(-1); });
      }
      qty.addEventListener("input", function () { sync(false); });
      qty.addEventListener("blur", function () { sync(true); });
      qty.addEventListener("change", function () { sync(true); });
      sync(true);
    });
  }

  /* --------------------------------------------------- add-to-cart UX --- */
  /* The forms post natively. This only adds a pressed state and an
     announcement, so a slow network never looks like a dead button. */
  /* Every Medicora buy form used to be a plain POST to /cart/add, so adding a
     product meant a full page navigation that landed the shopper on the cart
     page — the cart drawer, the free-shipping progress and the cart suggestion
     were never seen from a product page at all.

     These now post to /cart/add.js, re-render the drawer section from the
     server and open Horizon's own drawer. It is the same request to the same
     Shopify endpoint, so the cart-add analytics event still fires exactly once
     and no tracking is added, removed or duplicated here. If fetch fails for
     any reason the form is submitted natively, so the old behaviour is the
     fallback rather than a dead button. */
  var CART_DRAWER_SECTION = "cart-drawer-section";

  function openCartDrawer() {
    var toggle = qsa("button").filter(function (b) {
      return b.getAttribute("on:click") === "#cart-drawer/toggle";
    })[0];
    if (toggle) toggle.click();
  }

  function sectionHost(id) {
    return document.getElementById("shopify-section-" + id);
  }

  /* Re-renders whichever cart surfaces are on the page from the server, so the
     line items, the subtotal, the free-shipping progress and the upsell card
     all come back consistent with the real cart rather than being patched up
     here one at a time. On the cart page that is the main cart section; the
     drawer exists on every page. */
  function cartSectionIds() {
    var ids = [];
    if (sectionHost(CART_DRAWER_SECTION)) ids.push(CART_DRAWER_SECTION);
    qsa('[id^="shopify-section-"]').forEach(function (el) {
      var id = el.id.replace("shopify-section-", "");
      if (el.querySelector(".cart-page") && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  }

  function drawerIsOpen() {
    var host = sectionHost(CART_DRAWER_SECTION);
    return !!(host && host.querySelector("dialog[open]"));
  }

  function refreshCart() {
    var ids = cartSectionIds();
    if (!ids.length) return Promise.resolve();
    /* Replacing the drawer section's markup destroys the open <dialog> with it,
       which closed the drawer the moment the upsell was added — exactly the
       thing that must not happen. Remember the open state and restore it once
       the fresh markup is in and its custom elements have upgraded. */
    var wasOpen = drawerIsOpen();
    return fetch(window.Shopify.routes.root + "?sections=" + ids.join(","), {
      headers: { Accept: "application/json" }
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        ids.forEach(function (id) {
          var html = data[id];
          var host = sectionHost(id);
          if (!html || !host) return;
          var parsed = new DOMParser().parseFromString(html, "text/html");
          var fresh = parsed.body.firstElementChild;
          if (fresh) host.innerHTML = fresh.innerHTML;
        });
        /* the freshly injected markup needs its own listeners */
        init(document);
        if (wasOpen) {
          window.setTimeout(function () {
            if (!drawerIsOpen()) openCartDrawer();
          }, 60);
        }
      });
  }

  /* kept as the old name for the buy forms that already call it */
  function refreshCartDrawer() { return refreshCart(); }

  /* The cart route as Liquid rendered it on <html> from {{ routes.cart_url }},
     so it carries the shop's locale and market prefix. Nothing here builds a
     URL or names a domain; with no value present the caller keeps the old
     drawer behaviour rather than guessing. */
  function medicoraCartUrl() {
    var url = document.documentElement.getAttribute("data-medicora-cart-url");
    if (url) return url;
    if (window.Theme && window.Theme.routes && window.Theme.routes.cart_url) {
      return window.Theme.routes.cart_url;
    }
    return null;
  }

  function showFormError(form, message) {
    var el = qs("[data-medicora-add-error]", form);
    if (!el) {
      el = document.createElement("p");
      el.setAttribute("data-medicora-add-error", "");
      el.setAttribute("role", "alert");
      form.appendChild(el);
    }
    el.textContent = message;
    el.hidden = false;
    window.MedicoraAnnounce(message);
  }

  function bindAddToCart(root) {
    qsa("[data-medicora-add], [data-sticky-form]", root).forEach(function (form) {
      if (!claim(form, "medicoraAdd")) return;
      form.addEventListener("submit", function (event) {
        var btn = qs("button[type=submit]", form);
        if (btn && btn.dataset.busy) return;
        if (!window.fetch || !window.FormData || !window.DOMParser) return;

        event.preventDefault();
        var navigating = false;
        if (btn) {
          btn.dataset.busy = "1";
          btn.setAttribute("aria-busy", "true");
          btn.setAttribute("data-medicora-loading", "1");
          btn.disabled = true;
        }
        window.MedicoraAnnounce("Adding to your cart");

        fetch(window.Shopify.routes.root + "cart/add.js", {
          method: "POST",
          body: new FormData(form),
          headers: { Accept: "application/json" }
        })
          .then(function (r) {
            if (!r.ok) throw new Error("add failed");
            return r.json();
          })
          .then(function (item) {
            window.MedicoraAnnounce((item && item.title ? item.title : "Item") + " added to your cart");
            /* Shopify has confirmed the line is in the cart, so this is the
               successful response and not a guess. The drawer is no longer
               opened here: the customer goes to the full cart page instead.
               No second request is made — the add above is the only one. */
            var target = medicoraCartUrl();
            if (!target) { return openCartDrawer(); }
            navigating = true;
            window.location.assign(target);
          })
          .catch(function () {
            /* The add did not succeed. Deliberately no fallback form.submit():
               a retry here could post the same item a second time if the first
               request reached Shopify before the connection dropped. */
            showFormError(form, "We could not add this to your cart. Please try again.");
          })
          .then(function () {
            /* Leave the button locked while the browser navigates, so a second
               tap during the page change cannot fire another add. */
            if (navigating) return;
            if (btn) {
              delete btn.dataset.busy;
              btn.removeAttribute("aria-busy");
              btn.removeAttribute("data-medicora-loading");
              btn.disabled = false;
            }
          });
      });
    });
  }

  /* ----------------------------------------------------- cart upsell ---- */
  /* One-click add for the cart's single suggestion.

     Everything the button needs is on the card as data attributes, so this
     rebinds correctly every time Horizon re-renders and morphs the cart
     section — which it does on every quantity change, removal and add. Binding
     is claimed per element, so a re-render cannot leave two listeners on one
     button and add the item twice.

     It uses the same /cart/add.js endpoint and the same drawer refresh as
     every other Medicora buy form, so there is one cart-add analytics event and
     nothing here touches the pixel, the discount field, the quantity
     selectors, the remove buttons or the checkout button. */

  function upsellPayload(card) {
    return {
      product_id: card.getAttribute("data-upsell-product-id") || "",
      variant_id: card.getAttribute("data-upsell-variant") || "",
      product_title: card.getAttribute("data-upsell-title") || "",
      price: Number(card.getAttribute("data-upsell-price") || 0) / 100,
      currency: card.getAttribute("data-upsell-currency") || "",
      cart_subtotal: Number(card.getAttribute("data-upsell-cart-total") || 0) / 100
    };
  }

  /* A small, dependency-free data layer. If the page already has one — a
     dataLayer array, or Shopify's analytics — the event goes there too. Never a
     Meta standard event: adding to a cart is not a purchase, and this fires no
     fbq call of its own. */
  function upsellEvent(name, card) {
    var detail = upsellPayload(card);
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: name }, detail));
      document.dispatchEvent(new CustomEvent(name, { detail: detail }));
    } catch (e) { /* analytics must never break the cart */ }
  }

  function bindCartUpsell(root) {
    qsa("[data-cart-upsell]", root).forEach(function (card) {
      if (!claim(card, "medicoraUpsell")) return;

      /* one view event per rendered card, not per re-render of the same cart */
      var key = card.getAttribute("data-upsell-variant");
      if (window.MedicoraUpsellSeen !== key) {
        window.MedicoraUpsellSeen = key;
        upsellEvent("cart_upsell_view", card);
      }

      var form = qs("[data-upsell-form]", card);
      var btn = qs("[data-upsell-btn]", card);
      var label = qs(".cart-upsell__btn-label", card);
      var error = qs("[data-upsell-error]", card);
      if (!form || !btn || !label) return;

      form.addEventListener("submit", function (event) {
        /* the guard is on the element, so a double tap, an Enter keypress and a
           second click during the request all collapse into one add */
        if (card.dataset.busy === "1" || card.dataset.done === "1") {
          event.preventDefault();
          return;
        }
        if (!window.fetch || !window.FormData) return; /* native post fallback */

        event.preventDefault();
        card.dataset.busy = "1";
        btn.disabled = true;
        btn.setAttribute("aria-busy", "true");
        card.classList.add("is-loading");
        if (error) error.hidden = true;
        upsellEvent("cart_upsell_click", card);

        fetch(window.Shopify.routes.root + "cart/add.js", {
          method: "POST",
          body: new FormData(form),
          headers: { Accept: "application/json" }
        })
          .then(function (r) {
            if (!r.ok) throw new Error("add failed");
            return r.json();
          })
          .then(function () {
            card.dataset.done = "1";
            card.classList.remove("is-loading");
            label.textContent = "ADDED ✓";
            card.classList.add("is-added");
            upsellEvent("cart_upsell_added", card);
            window.MedicoraAnnounce("Silicone Body Applicator added to your cart");

            /* Let the ADDED state read for a moment, then let the server
               re-render the cart. The fresh markup drops the card on its own,
               because the snippet's own condition no longer holds once the
               applicator is a line item — the collapse is not faked here. */
            return new Promise(function (resolve) {
              window.setTimeout(function () {
                card.classList.add("is-collapsing");
                resolve(refreshCart());
              }, 650);
            });
          })
          .catch(function () {
            card.dataset.busy = "";
            card.classList.remove("is-loading");
            btn.disabled = false;
            btn.removeAttribute("aria-busy");
            if (error) error.hidden = false;
            window.MedicoraAnnounce("Could not add this item. Please try again.");
          });
      });
    });
  }

  /* ------------------------------------------------------ sticky buy ---- */
  /* Shown once the in-page Add to cart button has left the viewport, hidden
     again once the last one comes back into view, so the bar never sits on top
     of the call to action it duplicates.

     Deliberately plain scroll maths rather than IntersectionObserver: an
     observer misses states on instant scrolls (anchor jumps, restored scroll
     position, a phone flicked hard), and this bar showing at the wrong moment
     is more noticeable than the animation it replaces. */
  function bindStickyBuy() {
    var bar = qs("[data-sticky-buy]");
    if (!bar || !claim(bar, "medicoraSticky")) return;
    /* The bar's own form is excluded, and the page forms are ordered by their
       real position rather than by DOM order — the bar is fixed, so document
       order says nothing about what the customer actually sees last. */
    var forms = qsa("form[data-medicora-add]").filter(function (f) {
      return !bar.contains(f);
    }).sort(function (a, b) {
      return (a.getBoundingClientRect().top + window.scrollY) -
             (b.getBoundingClientRect().top + window.scrollY);
    });
    if (!forms.length) return;
    var first = forms[0];
    var shown = null;
    var ticking = false;

    function apply() {
      var vh = window.innerHeight;
      var pastFirst = first.getBoundingClientRect().bottom < 0;
      /* The rule is simply "is a real Add to cart on screen right now". An
         earlier version hid the bar once the LAST form had been scrolled past,
         which on this page meant the bundle module's button 900px down turned
         it off for the rest of the page. */
      var ctaOnScreen = forms.some(function (f) {
        var r = f.getBoundingClientRect();
        return r.bottom > 0 && r.top < vh * 0.95;
      });
      /* The bar must also stand down while the cart drawer is open: on a phone
         the drawer is full-height and a fixed bar would sit on top of its
         checkout button. */
      var show = pastFirst && !ctaOnScreen && !drawerIsOpen();
      if (show === shown) return;
      shown = show;
      bar.classList.toggle("is-in", show);
      bar.setAttribute("aria-hidden", String(!show));
      qsa("button, input, a", bar).forEach(function (el) {
        if (show) el.removeAttribute("tabindex");
        else el.setAttribute("tabindex", "-1");
      });
      document.body.classList.toggle("has-sticky-buy", show);
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () { ticking = false; apply(); });
    }

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    /* Scroll maths alone is one input short: it depends on the page actually
       receiving scroll events, and the state that matters here — "did an Add
       to cart just enter or leave the viewport" — is exactly what an
       IntersectionObserver reports natively, straight from layout. Both are
       wired: the observer settles the crossings, the scroll listener keeps
       the bar correct during momentum and on restored scroll positions. */
    if (window.IntersectionObserver) {
      var io = new IntersectionObserver(function () { apply(); }, {
        threshold: [0, 0.01, 1],
        rootMargin: "0px 0px -5% 0px"
      });
      forms.forEach(function (f) { io.observe(f); });
    }
    /* The drawer opens and closes without a scroll event, so watch it directly.
       One observer on the drawer host, not a timer. */
    var drawerHost = sectionHost(CART_DRAWER_SECTION);
    if (drawerHost && window.MutationObserver) {
      new MutationObserver(onScroll).observe(drawerHost, {
        attributes: true, attributeFilter: ["open"], subtree: true
      });
    }
    apply();
  }

  /* -------------------------------------------------------- watchdog ---- */
  /* Last line of defence for the blank-section failure mode. If the observer
     never fires — a browser quirk, an exception thrown earlier in this file, a
     section injected after the observer was built — anything still hidden is
     shown outright after a short delay. A missed animation is invisible to the
     customer; a permanently blank section is a lost order. */
  if (!window.MedicoraRevealWatchdog) {
    window.MedicoraRevealWatchdog = true;
    window.setTimeout(function () { showAll(document); }, 1500);
    window.addEventListener("load", function () {
      window.setTimeout(function () { showAll(document); }, 400);
    });
  }

  /* ------------------------------------------------------------ init ---- */
  function init(root) {
    bindAccordions(root);
    bindGallery(root);
    bindQuantity(root);
    bindAddToCart(root);
    bindStickyBuy();
    bindCartUpsell(root);
    if (revealObserver) window.MedicoraRevealRefresh(root);
    else showAll(root);
  }

  window.MedicoraInit = init;

  function boot() { init(document); }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  /* The theme editor re-renders one section at a time. Without this the
     re-rendered section comes back with its .reveal elements at opacity:0 and
     its accordions dead — which is exactly what a merchant sees as "the
     section went blank while I was editing it". */
  if (!window.MedicoraSectionHookReady) {
    window.MedicoraSectionHookReady = true;
    var designMode = !!(window.Shopify && window.Shopify.designMode);
    function reinit(event) {
      var root = event.target || document;
      init(root);
      /* In the editor the merchant is looking straight at the section they
         just changed, so waiting for an intersection to reveal it reads as
         "the section went blank". On the storefront the observer stays in
         charge and the animation is untouched. */
      if (designMode) showAll(root);
    }
    document.addEventListener("shopify:section:load", reinit);
    document.addEventListener("shopify:section:select", reinit);
  }
})();
